import { useState, type FormEvent } from 'react';
import { login } from '../../api/auth';
import { ApiError } from '../../api/client';
import { apiErrorMessage } from '../../api/errorMessage.ts';
import { challengeErrorKey, loginFallbackKey } from './loginError.ts';
import type { StrapiUser } from '../../api/types';
import type { MessageKey } from '../../i18n/messages.en.ts';
import { useT } from '../../i18n/i18n.tsx';
import { Button, TextField, InlineAlert } from '../../ui';
import { TurnstileWidget } from '../guest/TurnstileWidget';
import { SIGNUP_PATH } from '../../app/route';
import { navigate } from '../../app/useRoute';
import { useSignupOpen } from './useSignupOpen.ts';
import { CheckEmail } from './CheckEmail.tsx';
import './LoginDialog.css';

/**
 * DESIGN.md §9.3 Auth `LoginDialog`. Strapi Users & Permissions only (ARCHITECTURE.md §8) — no
 * "forgot password" affordance, and Strapi's own `/auth/local/register` stays disabled server-side.
 * MVP-278: the one way to make an account from here is the self-serve shop sign-up (`/signup`,
 * `POST /api/signup`), linked below the form.
 *
 * MVP-11 #28: this is a page, not a dialog — it is the entire return value of `App.tsx` while
 * signed out (no `AppShell`, no rail/header around it), with no `onClose`, no focus trap and no
 * Escape handling. It used to carry `role="dialog"` anyway (a modal role with none of a modal's
 * behaviour) and title itself with the repo slug. Plain landmark markup + a product name fix that.
 */
export function LoginDialog({
  onSuccess,
  reason,
}: {
  onSuccess: (user: StrapiUser) => void;
  // MVP-318: a dictionary key, not a finished sentence — `App.tsx`'s `onUnauthorized` listener used
  // to hand this a raw English string built inline, which rendered unchanged regardless of locale.
  reason?: MessageKey;
}) {
  const t = useT();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * MVP-471. Null until the server actually asks: `loginGuard` challenges from the third attempt
   * after two failures for this address, and the 403 carries the site key with it — the login page
   * has no other call to learn one from, and baking it into this bundle would make turning
   * Turnstile on a frontend redeploy instead of a backend .env change.
   */
  const [challengeSiteKey, setChallengeSiteKey] = useState<string | null>(null);
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  // A Turnstile token is single-use. Bumping this remounts the widget after every attempt that
  // consumed one, so the next try gets a fresh challenge instead of replaying a spent token that
  // Cloudflare now answers `timeout-or-duplicate` to.
  const [challengeRound, setChallengeRound] = useState(0);
  // MVP-278 harden: the link only when the server says sign-up is open.
  const signupOpen = useSignupOpen();
  // MVP-278 email verification: the address a self-serve account still has to confirm. The
  // username of such an account IS its email, so what was typed is where the link goes.
  const [unconfirmed, setUnconfirmed] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (loading) return;
    setError(null);
    setUnconfirmed(null);
    setLoading(true);
    try {
      const user = await login(identifier.trim(), password, challengeToken ?? undefined);
      onSuccess(user);
    } catch (err) {
      // MVP-471: the challenge, asked for or failed. Not a credentials problem — say so in its own
      // words and put the widget on screen, rather than telling somebody their correct password is
      // wrong.
      // MVP-645 adds `CHALLENGE_UNAVAILABLE` to the same branch: it also carries the site key and
      // it also is not a credentials problem, and branching on two of the three left the widget off
      // the screen for the third.
      const challengeKey = err instanceof ApiError ? challengeErrorKey(err.code) : null;
      if (challengeKey) {
        const siteKey = err instanceof ApiError ? err.details?.turnstileSiteKey : undefined;
        if (typeof siteKey === 'string') setChallengeSiteKey(siteKey);
        setChallengeToken(null);
        setChallengeRound((round) => round + 1);
        setError(t(challengeKey));
        return;
      }
      if (challengeToken) {
        // Any other outcome still burned the token this attempt carried.
        setChallengeToken(null);
        setChallengeRound((round) => round + 1);
      }
      // MVP-371: anything that is not a bad-credentials 400/401 used to show `err.message` — and
      // on THIS endpoint that message is always the users-permissions plugin's English, to a
      // signed-out reader with nobody beside them.
      //
      // MVP-645: every refusal now goes through `apiErrorMessage` first, so a named code (the
      // account-window 403s) gets its own sentence, and `loginFallbackKey` decides the rest by
      // whether the server ANSWERED. The old shape sent every 403 to "could not reach the server,
      // try again" — the wrong instruction, repeated forever, in front of a correct password.
      setError(apiErrorMessage(err, t(loginFallbackKey(err)), t));
      if (err instanceof ApiError && err.code === 'EMAIL_NOT_CONFIRMED') setUnconfirmed(identifier.trim());
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-screen">
      {/* MVP-11 #11: `aria-labelledby` on a role-less <div> is ARIA-prohibited (no naming a generic
          element) — `<section>` gives it an implicit landmark role the name can attach to. */}
      <section className="login-dialog" aria-labelledby="login-title">
        <div className="login-dialog__heading">
          <img className="login-dialog__logo" src="/logo-rms.png" alt={t('brand.logoAlt')} width={456} height={194} />
          <h1 id="login-title" className="login-dialog__title text-title">
            RMS Karaoke
          </h1>
          <p className="login-dialog__subtitle text-body">{t('auth.login.subtitle')}</p>
        </div>

        {reason && <InlineAlert variant="info">{t(reason)}</InlineAlert>}
        {error && <InlineAlert variant="danger">{error}</InlineAlert>}
        {unconfirmed && <CheckEmail key={unconfirmed} email={unconfirmed} />}

        <form className="login-dialog__form" onSubmit={handleSubmit}>
          <TextField
            label={t('auth.login.identifier')}
            name="identifier"
            type="text"
            autoComplete="username"
            autoFocus
            required
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
          />
          <TextField
            label={t('auth.login.password')}
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {challengeSiteKey && (
            <TurnstileWidget key={challengeRound} siteKey={challengeSiteKey} onToken={setChallengeToken} />
          )}
          {/* Deliberately not disabled while the challenge is unsolved: the widget can fail to load
              on a blocked network, and a dead Sign in button would leave whoever is standing at the
              venue PC with nothing to press and nothing to read. Submitting without a token gets
              the same 403 back, which at least says what is wrong. */}
          <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
            {t('auth.login.submit')}
          </Button>
        </form>

        {/* MVP-278: the one way into the self-serve sign-up page. A `ui/Button`, not a text link,
            so it gets the same control height as everything else on this card. */}
        {signupOpen && (
          <Button variant="ghost" fullWidth onClick={() => navigate(SIGNUP_PATH)}>
            {t('auth.login.signup')}
          </Button>
        )}

        {/* MVP-292: the same number the status bar shows, from the same build-time source
            (`vite.config.ts` `define` <- `web/package.json`), because the status bar is behind this
            screen. Whoever is standing at a venue PC that cannot sign in is exactly the person being
            asked which build it runs, and until now they had no way to answer. Not `/api/health`'s
            `version` — that is `info.strapi`, the backend framework's, and App.tsx already argues
            this at length. A version number is not copy, so it names no dictionary key.
            MVP-294: no `text-meta` — the corner stamp carries its own size (`--fs-stamp-*`). */}
        <p className="login-dialog__version tabular-nums">{`v${__APP_VERSION__}`}</p>
      </section>
    </main>
  );
}
